<template>
  <v-row>
    <!-- Sol: Dosya yükleme + aksiyonlar -->
    <v-col cols="12" md="5">
      <v-card>
        <v-card-title class="text-body-1">Dosya Yükle & Çalıştır</v-card-title>
        <v-card-text>
          <!-- Tip seçimi -->
          <div class="text-caption mb-1 text-medium-emphasis">Belge Türü</div>
          <v-btn-toggle v-model="docType" mandatory color="primary" variant="outlined" density="compact" class="mb-4">
            <v-btn value="laws">Yasa / Tüzük</v-btn>
            <v-btn value="cases">Mahkeme Kararı</v-btn>
          </v-btn-toggle>

          <!-- Drag & drop -->
          <div
            class="drop-zone"
            :class="{ 'drag-over': isDragging }"
            @dragover.prevent="isDragging = true"
            @dragleave="isDragging = false"
            @drop.prevent="onDrop"
            @click="fileInput?.click()"
          >
            <span class="material-symbols-outlined" style="font-size:2.5rem;opacity:.5">upload_file</span>
            <div class="text-body-2 mt-1">PDF veya DOCX dosyalarını sürükleyin</div>
            <div class="text-caption text-medium-emphasis">veya tıklayarak seçin</div>
            <input ref="fileInput" type="file" multiple accept=".pdf,.docx" style="display:none" @change="onFileInput" />
          </div>

          <!-- Seçilen dosyalar -->
          <div v-if="files.length" class="mt-2">
            <div v-for="(f, i) in files" :key="i" class="d-flex align-center justify-space-between py-1">
              <span class="text-caption text-truncate" style="max-width:200px">{{ f.name }}</span>
              <v-btn icon size="x-small" variant="plain" @click="removeFile(i)">
                <span class="material-symbols-outlined" style="font-size:16px">close</span>
              </v-btn>
            </div>
          </div>
        </v-card-text>

        <v-divider />

        <!-- Aksiyon butonları -->
        <v-card-text>
          <v-btn
            block
            color="primary"
            class="mb-2"
            :disabled="busy || files.length === 0"
            :loading="busy && activeAction === 'pipeline'"
            @click="runPipeline"
          >
            <span class="material-symbols-outlined mr-1">play_arrow</span>
            Pipeline Çalıştır
          </v-btn>

          <v-btn
            block
            color="secondary"
            variant="outlined"
            class="mb-2"
            :disabled="busy"
            :loading="busy && activeAction === 'enrich'"
            @click="runEnrich('all')"
          >
            <span class="material-symbols-outlined mr-1">auto_awesome</span>
            Enrich Çalıştır
          </v-btn>

          <v-menu>
            <template #activator="{ props }">
              <v-btn block variant="outlined" :disabled="busy" v-bind="props">
                <span class="material-symbols-outlined mr-1">database</span>
                İndeks Yenile
                <span class="material-symbols-outlined ml-1">arrow_drop_down</span>
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item title="Sadece BM25" @click="runRebuild('bm25-only')" />
              <v-list-item title="Yasalar" @click="runRebuild('laws')" />
              <v-list-item title="Kararlar" @click="runRebuild('cases')" />
              <v-list-item title="Tümü" @click="runRebuild('all')" />
            </v-list>
          </v-menu>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Sağ: Log terminali -->
    <v-col cols="12" md="7">
      <v-card style="height:100%">
        <v-card-title class="d-flex align-center justify-space-between text-body-1">
          <span>Log</span>
          <v-btn size="x-small" variant="plain" icon @click="logs = []">
            <span class="material-symbols-outlined">delete_sweep</span>
          </v-btn>
        </v-card-title>
        <v-card-text style="padding:0">
          <div ref="logEl" class="log-terminal">
            <div v-if="!logs.length" class="text-medium-emphasis text-caption pa-4">
              Henüz çıktı yok.
            </div>
            <div
              v-for="(line, i) in logs"
              :key="i"
              class="log-line"
              :class="`log-${line.level}`"
            >
              <span v-if="line.pct !== undefined" class="log-pct">[{{ line.pct }}%]</span>
              {{ line.msg }}
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import { ref, nextTick } from 'vue'

interface LogLine {
  level: string
  msg: string
  pct?: number
}

const API_BASE = process.env.VUE_APP_API_BASE_URL as string

const docType      = ref<'laws' | 'cases'>('laws')
const files        = ref<File[]>([])
const fileInput    = ref<HTMLInputElement | null>(null)
const isDragging   = ref(false)
const busy         = ref(false)
const activeAction = ref<string>('')
const logs         = ref<LogLine[]>([])
const logEl        = ref<HTMLElement | null>(null)

function onDrop(e: DragEvent) {
  isDragging.value = false
  const dropped = Array.from(e.dataTransfer?.files ?? []).filter(f =>
    f.name.endsWith('.pdf') || f.name.endsWith('.docx')
  )
  files.value.push(...dropped)
}

function onFileInput(e: Event) {
  const input = e.target as HTMLInputElement
  files.value.push(...Array.from(input.files ?? []))
  input.value = ''
}

function removeFile(i: number) {
  files.value.splice(i, 1)
}

function addLog(level: string, msg: string, pct?: number) {
  logs.value.push({ level, msg, pct })
  nextTick(() => {
    if (logEl.value) {
      logEl.value.scrollTop = logEl.value.scrollHeight
    }
  })
}

async function readSSE(response: Response) {
  const reader = response.body!.getReader()
  const decoder = new TextDecoder()
  let buf = ''

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buf += decoder.decode(value, { stream: true })
    const parts = buf.split('\n\n')
    buf = parts.pop() ?? ''
    for (const part of parts) {
      const line = part.trim()
      if (line.startsWith('data: ')) {
        try {
          const payload = JSON.parse(line.slice(6))
          addLog(payload.level ?? 'info', payload.msg ?? '', payload.pct)
        } catch {
          addLog('info', line.slice(6))
        }
      }
    }
  }
}

async function runPipeline() {
  busy.value = true
  activeAction.value = 'pipeline'
  addLog('info', `Pipeline başlatılıyor (${docType.value})...`)

  const fd = new FormData()
  fd.append('type', docType.value)
  files.value.forEach(f => fd.append('files[]', f))

  const token = localStorage.getItem('auth_token')

  try {
    const res = await fetch(`${API_BASE}/admin/parser/pipeline/run`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    })
    addLog('info', `HTTP status: ${res.status}`)
    await readSSE(res)
    addLog('done', '✓ Pipeline tamamlandı.')
  } catch (e: any) {
    addLog('error', `Hata: ${e.message}`)
  } finally {
    busy.value = false
    activeAction.value = ''
  }
}

async function ssePost(path: string, body: object, label: string) {
  busy.value = true
  activeAction.value = label
  addLog('info', `${label} başlatılıyor...`)

  const token = localStorage.getItem('auth_token')

  try {
    const res = await fetch(`${API_BASE}/admin/parser/${path}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })
    await readSSE(res)
    addLog('done', `✓ ${label} tamamlandı.`)
  } catch (e: any) {
    addLog('error', `Hata: ${e.message}`)
  } finally {
    busy.value = false
    activeAction.value = ''
  }
}

function runEnrich(mode: string) {
  ssePost('enrich/run', { mode }, 'enrich')
}

function runRebuild(scope: string) {
  ssePost('index/rebuild', { scope }, 'rebuild')
}
</script>

<style scoped>
.drop-zone {
  border: 2px dashed rgba(var(--v-theme-on-surface), 0.2);
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: border-color .2s, background .2s;
}
.drop-zone:hover,
.drop-zone.drag-over {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), .05);
}

.log-terminal {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  background: #0d0d0d;
  color: #e0e0e0;
  min-height: 360px;
  max-height: 500px;
  overflow-y: auto;
  padding: 8px 12px;
  white-space: pre-wrap;
  word-break: break-all;
}
.log-line { line-height: 1.6; }
.log-error  { color: #ff6b6b; }
.log-done   { color: #69db7c; font-weight: bold; }
.log-pct    { color: #ffd43b; margin-right: 6px; }
</style>
